import { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useCartStore } from '../stores/cartStore';
import { useSearchStore } from '../stores/useSearchStore';
import { useFilterStore } from '../stores/useFilterStore';
import { useLanguage } from '../hooks/useLanguage';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  ShoppingCart,
  Menu,
  User,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { getMobileInputProps } from '../utils/mobile';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { SearchResultsPopup } from './SearchResultsPopup';
import { MobileSearchPopup } from './MobileSearchPopup';
import { useDebounce } from '../hooks/useDebounce';
import type { Product } from './ProductCard';
import type { Recipe } from '../hooks/useShopifySearch';

// Lazy loaded components for better performance
const MegaMenu = lazy(() => import('./MegaMenu').then(module => ({ default: module.MegaMenu })));
const MobileMenu = lazy(() => import('./MobileMenu').then(module => ({ default: module.MobileMenu })));

interface HeaderProps {
  onCartClick?: () => void;
  onCatalogClick?: () => void;
  onRecipesClick?: () => void;
  onMenuClick?: () => void;
  onAccountSettingsClick?: () => void;
  onPageNavigation?: (page: string) => void;
  onProductClick?: (product: Product) => void;
  onRecipeClick?: (recipe: Recipe) => void;
  onAddToCart?: (product: Product) => void;
}

// Categories will be defined inside the component to access t() function

export function Header({
  onCartClick,
  onCatalogClick,
  onRecipesClick,
  onMenuClick,
  onAccountSettingsClick,
  onPageNavigation,
  onProductClick,
  onRecipeClick,
  onAddToCart,
}: HeaderProps) {
  const navigate = useNavigate();
  const { getLocalizedUrl } = useLanguage();
  const { t } = useTranslation('common');
  const { t: tNav } = useTranslation('navigation');

  // Mobile menu categories with i18n
  const mobileMenuCategories = [
    {
      id: 'popular-dishes',
      name: tNav('categories.popularDishes.name'),
      items: [
        tNav('categories.popularDishes.items.forPizza'),
        tNav('categories.popularDishes.items.forFrenchFries'),
        tNav('categories.popularDishes.items.forKebabBBQ'),
        tNav('categories.popularDishes.items.forPasta'),
        tNav('categories.popularDishes.items.forSteak'),
        tNav('categories.popularDishes.items.forFriedChicken'),
        tNav('categories.popularDishes.items.forGrilledFish'),
        tNav('categories.popularDishes.items.forRoastedPotatoes'),
        tNav('categories.popularDishes.items.forGrilledVegetables'),
        tNav('categories.popularDishes.items.forSoups'),
      ],
    },
    {
      id: 'kitchen-essentials',
      name: tNav('categories.kitchenEssentials.name'),
      items: [
        tNav('categories.kitchenEssentials.items.basicSpices'),
        tNav('categories.kitchenEssentials.items.essentialHerbs'),
        tNav('categories.kitchenEssentials.items.aromaticBases'),
        tNav('categories.kitchenEssentials.items.readyMadeBlends'),
      ],
    },
    {
      id: 'spice-blends',
      name: tNav('categories.spiceBlends.name'),
      items: [
        tNav('categories.spiceBlends.items.regionalBlends'),
        tNav('categories.spiceBlends.items.universalSeasonings'),
        tNav('categories.spiceBlends.items.chiliBlends'),
        tNav('categories.spiceBlends.items.smokedBlends'),
      ],
    },
    {
      id: 'hot-peppers-chili',
      name: tNav('categories.hotPeppersChili.name'),
      items: [
        tNav('categories.hotPeppersChili.items.sweetMild'),
        tNav('categories.hotPeppersChili.items.moderatelyHot'),
        tNav('categories.hotPeppersChili.items.hot'),
        tNav('categories.hotPeppersChili.items.veryHot'),
      ],
    },
    {
      id: 'world-cuisines',
      name: tNav('categories.worldCuisines.name'),
      items: [
        tNav('categories.worldCuisines.items.italian'),
        tNav('categories.worldCuisines.items.indian'),
        tNav('categories.worldCuisines.items.asian'),
        tNav('categories.worldCuisines.items.mexican'),
        tNav('categories.worldCuisines.items.mediterranean'),
        tNav('categories.worldCuisines.items.french'),
        tNav('categories.worldCuisines.items.middleEastern'),
      ],
    },
    {
      id: 'dish-type',
      name: tNav('categories.dishType.name'),
      items: [
        tNav('categories.dishType.items.meatDishes'),
        tNav('categories.dishType.items.fishSeafood'),
        tNav('categories.dishType.items.vegetableDishes'),
        tNav('categories.dishType.items.soupsBroths'),
        tNav('categories.dishType.items.saladsAppetizers'),
      ],
    },
    {
      id: 'organic',
      name: tNav('categories.organic.name'),
      items: [
        tNav('categories.organic.items.organicSpices'),
        tNav('categories.organic.items.organicHerbs'),
        tNav('categories.organic.items.organicBlends'),
        tNav('categories.organic.items.organicChiliPeppers'),
      ],
      badge: tNav('badges.certified'),
    },
    {
      id: 'grill-bbq',
      name: tNav('categories.grillBBQ.name'),
      items: [
        tNav('categories.grillBBQ.items.dryRubBlends'),
        tNav('categories.grillBBQ.items.marinadeSpices'),
        tNav('categories.grillBBQ.items.meatBlends'),
      ],
    },
    {
      id: 'gift-sets',
      name: tNav('categories.giftSets.name'),
      items: [
        tNav('categories.giftSets.items.beginnerSets'),
        tNav('categories.giftSets.items.themedSets'),
        tNav('categories.giftSets.items.professionalSets'),
        tNav('categories.giftSets.items.giftSets'),
      ],
    },
    {
      id: 'baking',
      name: tNav('categories.baking.name'),
      items: [
        tNav('categories.baking.items.sweetSpices'),
        tNav('categories.baking.items.spiceBlends'),
        tNav('categories.baking.items.aromaticAdditives'),
      ],
    },
  ];

  const quickCategories = [
    { id: 'paprika', name: tNav('categories.paprika.name') },
    { id: 'flavored-salts', name: tNav('categories.flavoredSalts.name') },
    {
      id: 'sales-discounts',
      name: tNav('categories.salesDiscounts.name'),
      badge: tNav('badges.upToDiscount'),
    },
  ];

  // 🎯 Прямое подключение к CartStore - Header сам получает актуальное количество!
  const cartItemsCount = useCartStore(state => state.getTotalItems());

  // 🔍 Search store для сохранения поискового запроса
  const setSearchStoreQuery = useSearchStore(state => state.setQuery);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openCategories, setOpenCategories] = useState<string[]>([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [isMobileSearchPopupOpen, setIsMobileSearchPopupOpen] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const megaMenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const handleMegaMenuEnter = () => {
    if (megaMenuTimeoutRef.current) {
      clearTimeout(megaMenuTimeoutRef.current);
    }
    setIsMegaMenuOpen(true);
  };

  const handleMegaMenuLeave = () => {
    megaMenuTimeoutRef.current = setTimeout(() => {
      setIsMegaMenuOpen(false);
    }, 150);
  };

  const handleCatalogClick = () => {
    setIsMegaMenuOpen(false);
    setIsMobileMenuOpen(false);
    onCatalogClick?.();
  };

  // Helper function to map category IDs to Shopify collection handles by item index
  const getCategoryFilterMapping = (categoryId: string, itemIndex?: number) => {
    switch (categoryId) {
      case 'popular-dishes':
        if (itemIndex !== undefined) {
          // Map by index to handle localized text
          const dishHandles = [
            'for-pizza',
            'for-french-fries',
            'for-kebab-and-bbq-skewers',
            'for-pasta',
            'for-steak',
            'for-fried-chicken',
            'for-grilled-fish',
            'for-roasted-potatoes',
            'for-grilled-vegetables',
            'for-soups',
          ];
          return { type: 'collection', value: dishHandles[itemIndex] || null };
        }
        return { type: 'collection', value: 'popular-dishes' };
      
      case 'kitchen-essentials':
        if (itemIndex !== undefined) {
          const essentialsHandles = [
            'basic-spices',
            'essential-herbs',
            'aromatic-bases',
            'ready-made-blends',
          ];
          return { type: 'collection', value: essentialsHandles[itemIndex] || null };
        }
        return { type: 'collection', value: 'kitchen-essentials' };
      
      case 'spice-blends':
        if (itemIndex !== undefined) {
          const blendsHandles = [
            'regional-blends',
            'universal-seasonings',
            'chili-blends',
            'smoked-blends',
          ];
          return { type: 'collection', value: blendsHandles[itemIndex] || null };
        }
        return { type: 'collection', value: 'spice-blends' };
      
      case 'hot-peppers-chili':
        if (itemIndex !== undefined) {
          const heatHandles = [
            'sweet-and-mild-0-1000-scoville',
            'moderately-hot-1000-10000-scoville',
            'hot-10000-100000-scoville',
            'very-hot-100000-scoville',
          ];
          return { type: 'collection', value: heatHandles[itemIndex] || null };
        }
        return { type: 'collection', value: 'hot-peppers-and-chili' };
      
      case 'paprika':
        return { type: 'collection', value: 'paprika' };
      
      case 'organic':
        if (itemIndex !== undefined) {
          const organicHandles = [
            'organic-spices',
            'organic-herbs',
            'organic-blends',
            'organic-chili-and-peppers',
          ];
          return { type: 'collection', value: organicHandles[itemIndex] || null };
        }
        return { type: 'collection', value: 'organic' };
      
      case 'grill-bbq':
        if (itemIndex !== undefined) {
          const grillHandles = [
            'dry-rub-blends',
            'marinade-spices',
            'meat-blends',
          ];
          return { type: 'collection', value: grillHandles[itemIndex] || null };
        }
        return { type: 'collection', value: 'for-grill-and-bbq' };
      
      case 'gift-sets':
        if (itemIndex !== undefined) {
          const giftHandles = [
            'beginner-sets',
            'themed-sets',
            'professional-sets',
            'gift-sets',
          ];
          return { type: 'collection', value: giftHandles[itemIndex] || null };
        }
        return { type: 'collection', value: 'gift-sets' };
      
      case 'flavored-salts':
        return { type: 'collection', value: 'flavored-salts' };
      
      case 'world-cuisines':
        if (itemIndex !== undefined) {
          const cuisineHandles = [
            'italian-cuisine',
            'indian-cuisine',
            'asian-cuisine',
            'mexican-cuisine',
            'mediterranean-cuisine',
            'french-cuisine',
            'middle-eastern-cuisine',
          ];
          return { type: 'collection', value: cuisineHandles[itemIndex] || null };
        }
        // No direct world-cuisines collection, navigate to catalog with cuisine filter
        return { type: 'catalog', value: 'world-cuisines' };
      
      case 'dish-type':
        if (itemIndex !== undefined) {
          const dishTypeHandles = [
            'meat-dishes',
            'fish-and-seafood',
            'vegetable-dishes',
            'soups-and-broths',
            'salads-and-appetizers',
          ];
          return { type: 'collection', value: dishTypeHandles[itemIndex] || null };
        }
        // No direct dish-type collection, navigate to catalog with dish-type filter
        return { type: 'catalog', value: 'dish-type' };
      
      case 'baking':
        if (itemIndex !== undefined) {
          const bakingHandles = [
            'sweet-spices',
            'spice-blends',
            'aromatic-additives',
          ];
          return { type: 'collection', value: bakingHandles[itemIndex] || null };
        }
        return { type: 'collection', value: 'for-baking' };
      
      case 'sales-discounts':
        return { type: 'collection', value: 'sales-discounts' };
        
      default:
        return { type: 'collection', value: categoryId };
    }
  };

  // Helper function to get all subcollection handles for a main category (same as in MegaMenu)
  const getSubcollections = (categoryId: string): string[] => {
    switch (categoryId) {
      case 'popular-dishes':
        return [
          'for-pizza',
          'for-french-fries', 
          'for-kebab-and-bbq-skewers',
          'for-pasta',
          'for-steak',
          'for-fried-chicken',
          'for-grilled-fish',
          'for-roasted-potatoes',
          'for-grilled-vegetables',
          'for-soups'
        ];
      case 'kitchen-essentials':
        return [
          'basic-spices',
          'essential-herbs',
          'aromatic-bases',
          'ready-made-blends'
        ];
      case 'spice-blends':
        return [
          'regional-blends',
          'universal-seasonings',
          'chili-blends',
          'smoked-blends'
        ];
      case 'hot-peppers-chili':
        return [
          'sweet-and-mild-0-1000-scoville',
          'moderately-hot-1000-10000-scoville',
          'hot-10000-100000-scoville',
          'very-hot-100000-scoville'
        ];
      case 'organic':
        return [
          'organic-spices',
          'organic-herbs',
          'organic-blends',
          'organic-chili-and-peppers'
        ];
      case 'grill-bbq':
        return [
          'dry-rub-blends',
          'marinade-spices',
          'meat-blends'
        ];
      case 'gift-sets':
        return [
          'beginner-sets',
          'themed-sets',
          'professional-sets',
          'gift-sets'
        ];
      case 'world-cuisines':
        return [
          'italian-cuisine',
          'indian-cuisine',
          'asian-cuisine',
          'mexican-cuisine',
          'mediterranean-cuisine',
          'french-cuisine',
          'middle-eastern-cuisine'
        ];
      case 'dish-type':
        return [
          'meat-dishes',
          'fish-and-seafood',
          'vegetable-dishes',
          'soups-and-broths',
          'salads-and-appetizers'
        ];
      case 'baking':
        return [
          'sweet-spices',
          'spice-blends',
          'aromatic-additives'
        ];
      default:
        return [];
    }
  };

  const handleCategoryNavigation = (category: string, itemIndex?: number) => {
    const mapping = getCategoryFilterMapping(category, itemIndex);
    
    // Clear all filters first
    useFilterStore.getState().clearAllFilters();
    
    // Set collection filter
    if (mapping.value && mapping.type === 'collection') {
      if (itemIndex !== undefined) {
        // If clicking on specific subcategory, use just that collection
        useFilterStore.getState().setCategories([mapping.value]);
      } else {
        // If clicking on main category, include all its subcategories
        const subcollections = getSubcollections(category);
        if (subcollections.length > 0) {
          useFilterStore.getState().setCategories(subcollections);
        } else {
          useFilterStore.getState().setCategories([mapping.value]);
        }
      }
    }
    
    // Navigate to catalog with filters applied
    navigate(getLocalizedUrl('/catalog'));
    
    // Close menus
    setIsMegaMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileCategory = (categoryId: string) => {
    setOpenCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleMobileItemClick = (categoryId: string, itemIndex: number) => {
    handleCategoryNavigation(categoryId, itemIndex);
  };

  // Close mega menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        headerRef.current &&
        !headerRef.current.contains(event.target as Node)
      ) {
        setIsMegaMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (megaMenuTimeoutRef.current) {
        clearTimeout(megaMenuTimeoutRef.current);
      }
    };
  }, []);

  // Add blur effect when mobile menu is open (but not when search popup is open)
  useEffect(() => {
    const mainContent =
      document.querySelector('main') ||
      document.querySelector('#root') ||
      (document.body.children[0] as HTMLElement);

    if (isMobileMenuOpen && !isMobileSearchPopupOpen) {
      // Add blur effect to main content only if search popup is not open
      if (mainContent && mainContent !== document.body) {
        (mainContent as HTMLElement).style.filter = 'blur(2px)';
        (mainContent as HTMLElement).style.transition = 'filter 0.3s ease-out';
      }
    } else if (!isMobileSearchPopupOpen) {
      // Remove blur effect only if search popup is not managing it
      if (mainContent && mainContent !== document.body) {
        (mainContent as HTMLElement).style.filter = 'none';
      }
    }

    // Cleanup function - only remove if search popup is not open
    return () => {
      if (
        !isMobileSearchPopupOpen &&
        mainContent &&
        mainContent !== document.body
      ) {
        (mainContent as HTMLElement).style.filter = 'none';
      }
    };
  }, [isMobileMenuOpen, isMobileSearchPopupOpen]);

  // Handle scroll behavior for hiding/showing header
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Only apply scroll behavior on mobile (lg breakpoint and below)
      if (window.innerWidth >= 1024) {
        setIsHeaderHidden(false);
        return;
      }

      // Don't hide header when menus are open
      if (isMobileMenuOpen || isMobileSearchPopupOpen || isMegaMenuOpen) {
        return;
      }

      // Threshold for triggering hide/show (in pixels)
      const scrollThreshold = 10;

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down & past threshold
        if (Math.abs(currentScrollY - lastScrollY) > scrollThreshold) {
          setIsHeaderHidden(true);
        }
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up
        if (Math.abs(lastScrollY - currentScrollY) > scrollThreshold) {
          setIsHeaderHidden(false);
        }
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY, isMobileMenuOpen, isMobileSearchPopupOpen, isMegaMenuOpen]);

  return (
    <>
      <header
        ref={headerRef}
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out"
        style={{
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: isMobileMenuOpen ? 'none' : 'blur(20px)',
          WebkitBackdropFilter: isMobileMenuOpen ? 'none' : 'blur(20px)',
          filter: isMobileMenuOpen ? 'blur(2px)' : 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)',
          opacity: isHeaderHidden ? 0 : 1,
          pointerEvents: isHeaderHidden ? 'none' : 'auto',
          transition:
            'background 0.3s ease-out, backdrop-filter 0.3s ease-out, filter 0.3s ease-out, opacity 0.4s ease-out',
        }}
      >
        {/* Neural gradient overlay */}
        <div
          className="absolute inset-0 opacity-60"
          style={{
            background:
              'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(217, 119, 6, 0.02) 50%, rgba(180, 83, 9, 0.05) 100%)',
          }}
        />

        <div className="relative w-full px-4 lg:px-8">
          <div className="flex items-center h-20 w-full">
            {/* Logo Section - LEFT - Fixed width for consistent layout */}
            <div className="group w-auto lg:w-80 flex justify-start">
              <button
                onClick={() => navigate(getLocalizedUrl('/'))}
                className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-300"
              >
                <div
                  className="relative p-2 rounded-2xl transition-all duration-300 group-hover:scale-105"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.15) 100%)',
                    boxShadow: '0 4px 16px rgba(245, 158, 11, 0.2)',
                  }}
                >
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                    <span className="text-white font-bold text-lg">O</span>
                  </div>
                </div>
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-amber-900 via-amber-800 to-amber-900 bg-clip-text text-transparent">
                    Olispices
                  </h1>
                </div>
              </button>
            </div>

            {/* Primary Navigation - CENTER - Desktop Only - Takes remaining space */}
            <nav className="hidden lg:flex items-center space-x-2 flex-1 justify-center">
              <div
                className="relative group"
                onMouseEnter={handleMegaMenuEnter}
                onMouseLeave={handleMegaMenuLeave}
              >
                <button
                  className="group flex items-center gap-2 px-6 py-3 rounded-2xl text-gray-700 font-medium transition-all duration-300 hover:text-amber-900 relative overflow-hidden"
                  onClick={handleCatalogClick}
                  style={{
                    background: isMegaMenuOpen
                      ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.1) 100%)'
                      : 'transparent',
                  }}
                >
                  <span className="relative z-10">
                    {t('navigation.catalog')}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 transition-all duration-300 relative z-10 ${
                      isMegaMenuOpen
                        ? 'rotate-180 text-amber-700'
                        : 'group-hover:text-amber-700'
                    }`}
                  />

                  {/* Gradient hover effect */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(217, 119, 6, 0.05) 100%)',
                    }}
                  />
                </button>
              </div>

              {/* Navigation Items */}
              {[
                { label: t('navigation.recipes'), onClick: onRecipesClick },
                { label: t('navigation.menu'), onClick: onMenuClick },
                { label: t('navigation.subscription'), onClick: () => onPageNavigation('subscription') },
                { label: t('navigation.about'), onClick: () => onPageNavigation('about') },
              ].map(item => (
                <button
                  key={item.label}
                  onClick={item.onClick}
                  className="group relative px-6 py-3 rounded-2xl text-gray-700 font-medium transition-all duration-300 hover:text-amber-900 overflow-hidden"
                >
                  <span className="relative z-10">{item.label}</span>

                  {/* Gradient hover effect */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(217, 119, 6, 0.05) 100%)',
                    }}
                  />
                </button>
              ))}
            </nav>

            {/* Secondary Elements - RIGHT - Desktop - Fixed width to match left section */}
            <div className="hidden lg:flex items-center space-x-4 w-80 justify-end">
              {/* Advanced Search */}
              <div
                className={`relative transition-all duration-500 ${isSearchFocused ? 'w-56' : 'w-48'}`}
              >
                <div
                  className={`relative rounded-2xl transition-all duration-300 ${
                    isSearchFocused ? 'shadow-lg' : 'shadow-sm hover:shadow-md'
                  }`}
                  style={{
                    background: isSearchFocused
                      ? 'rgba(255, 255, 255, 0.95)'
                      : 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(10px)',
                    border: isSearchFocused
                      ? '2px solid rgba(245, 158, 11, 0.3)'
                      : '1px solid rgba(255, 255, 255, 0.3)',
                  }}
                >
                  <Search
                    className={`absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 transition-colors duration-300 ${
                      isSearchFocused ? 'text-amber-600' : 'text-gray-400'
                    }`}
                  />
                  <Input
                    placeholder={t('navigation.search')}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => {
                      // Delay blur to allow click events to fire first
                      setTimeout(() => setIsSearchFocused(false), 150);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && searchQuery.trim()) {
                        setSearchStoreQuery(searchQuery);
                        navigate(getLocalizedUrl('/search'));
                        setIsSearchFocused(false);
                      }
                    }}
                    className="pl-12 pr-4 py-3 bg-transparent border-0 focus:ring-0 placeholder:text-gray-500 text-gray-900 font-medium rounded-2xl"
                    {...getMobileInputProps()}
                  />

                  <SearchResultsPopup
                    searchQuery={debouncedSearchQuery}
                    isVisible={
                      isSearchFocused && debouncedSearchQuery.trim().length >= 3
                    }
                    onClose={() => setIsSearchFocused(false)}
                    onProductClick={product => {
                      onProductClick?.(product);
                      setIsSearchFocused(false);
                    }}
                    onRecipeClick={recipe => {
                      onRecipeClick?.(recipe);
                      setIsSearchFocused(false);
                    }}
                    onViewAllResults={query => {
                      // Сохраняем запрос в Zustand store
                      setSearchStoreQuery(query);
                      // Переходим на страницу поиска
                      navigate(getLocalizedUrl('/search'));
                      setIsSearchFocused(false);
                    }}
                    onAddToCart={product => {
                      onAddToCart?.(product);
                    }}
                  />
                </div>
              </div>

              {/* Profile - Icon Only */}
              <div className="relative group">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-12 w-12 rounded-2xl transition-all duration-300 hover:scale-105"
                  onClick={onAccountSettingsClick}
                  style={{
                    background: 'rgba(255, 255, 255, 0.6)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <User className="size-5 text-gray-600" size={20} />
                </Button>
              </div>

              {/* Cart */}
              <Button
                variant="ghost"
                size="icon"
                className="relative h-12 w-12 rounded-2xl transition-all duration-300 hover:scale-105 group"
                onClick={onCartClick}
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <ShoppingCart
                  className="size-5 text-gray-600 group-hover:text-amber-700 transition-colors"
                  size={20}
                />
                {cartItemsCount > 0 && (
                  <div
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{
                      background:
                        'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)',
                    }}
                  >
                    {cartItemsCount}
                  </div>
                )}
              </Button>
            </div>

            {/* Mobile Actions - Push to right edge */}
            <div className="flex items-center gap-3 lg:hidden ml-auto">
              {/* User Account - Mobile */}
              <Button
                variant="ghost"
                size="icon"
                className="h-12 w-12 rounded-xl transition-all duration-300"
                onClick={onAccountSettingsClick}
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <User className="size-5 text-gray-600" size={20} />
              </Button>

              {/* Cart - Mobile */}
              <Button
                variant="ghost"
                size="icon"
                className="relative h-12 w-12 rounded-xl transition-all duration-300"
                onClick={onCartClick}
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <ShoppingCart className="size-5 text-gray-600" size={20} />
                {cartItemsCount > 0 && (
                  <div
                    className="absolute -top-1 -right-1 h-5 w-5 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{
                      background:
                        'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)',
                    }}
                  >
                    {cartItemsCount}
                  </div>
                )}
              </Button>

              {/* Mobile Menu Button */}
              <Sheet
                open={isMobileMenuOpen}
                onOpenChange={setIsMobileMenuOpen}
                modal={false}
              >
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-12 w-12 rounded-xl transition-all duration-300"
                    style={{
                      background: 'rgba(255, 255, 255, 0.6)',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <Menu className="size-5 text-gray-600" size={20} />
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="left"
                  className="w-80 p-0 border-0"
                  style={{
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(25px)',
                    WebkitBackdropFilter: 'blur(25px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow:
                      '0 16px 64px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(245, 158, 11, 0.1)',
                  }}
                >
                  {/* Neural gradient overlay for mobile menu */}
                  <div
                    className="absolute inset-0 opacity-30 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(245, 158, 11, 0.03) 0%, rgba(217, 119, 6, 0.01) 50%, rgba(180, 83, 9, 0.03) 100%)',
                    }}
                  />

                  <div className="relative flex flex-col h-full">
                    <SheetHeader
                      className="px-6 py-4 relative"
                      style={{
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className="p-2 rounded-2xl"
                            style={{
                              background:
                                'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.15) 100%)',
                            }}
                          >
                            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
                              <span className="text-white font-bold text-lg">
                                O
                              </span>
                            </div>
                          </div>
                          <div>
                            <SheetTitle className="text-amber-900 text-xl font-bold">
                              Olispices
                            </SheetTitle>
                          </div>
                        </div>
                      </div>
                    </SheetHeader>

                    <div className="flex-1 overflow-y-auto">
                      {/* Mobile Search */}
                      <div
                        className="p-4 relative"
                        style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        <div
                          className="relative rounded-2xl"
                          style={{
                            background: 'rgba(255, 255, 255, 0.6)',
                            backdropFilter: 'blur(15px)',
                            WebkitBackdropFilter: 'blur(15px)',
                            border: '1px solid rgba(255, 255, 255, 0.25)',
                          }}
                        >
                          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                          <Input
                            placeholder={t('navigation.search')}
                            value={mobileSearchQuery}
                            onChange={e => setMobileSearchQuery(e.target.value)}
                            onClick={() => {
                              // Close mobile menu and open search popup only on explicit click
                              setIsMobileMenuOpen(false);
                              setIsMobileSearchPopupOpen(true);
                            }}
                            onKeyDown={e => {
                              if (
                                e.key === 'Enter' &&
                                mobileSearchQuery.trim()
                              ) {
                                setSearchStoreQuery(mobileSearchQuery);
                                navigate(getLocalizedUrl('/search'));
                                setIsMobileMenuOpen(false);
                              }
                            }}
                            className="pl-12 py-3 bg-transparent border-0 focus:ring-0"
                            {...getMobileInputProps()}
                          />
                        </div>
                      </div>

                      {/* Quick Navigation */}
                      <div
                        className="p-4 relative"
                        style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        <div className="space-y-2">
                          {[
                            {
                              label: t('navigation.recipes'),
                              onClick: () => {
                                setIsMobileMenuOpen(false);
                                onRecipesClick?.();
                              },
                            },
                            {
                              label: t('navigation.menu'),
                              onClick: () => {
                                setIsMobileMenuOpen(false);
                                onMenuClick?.();
                              },
                            },
                            {
                              label: tNav('mobile.subscription'),
                              onClick: () => {
                                setIsMobileMenuOpen(false);
                                document
                                  .querySelector('#subscription')
                                  ?.scrollIntoView({ behavior: 'smooth' });
                              },
                            },
                          ].map(item => (
                            <Button
                              key={item.label}
                              variant="ghost"
                              className="w-full justify-start h-12 text-base rounded-2xl transition-all duration-300 glass-medium border-white/30"
                              onClick={item.onClick}
                            >
                              {item.label}
                            </Button>
                          ))}
                        </div>
                      </div>

                      {/* Quick Categories */}
                      <div
                        className="p-4 relative"
                        style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        <h3 className="font-semibold text-gray-900 mb-3">
                          Quick Categories
                        </h3>
                        <div className="space-y-2">
                          {quickCategories.map(category => (
                            <Button
                              key={category.id}
                              variant="ghost"
                              className="w-full justify-between h-12 text-base rounded-2xl glass-medium border-white/30"
                              onClick={() =>
                                handleCategoryNavigation(category.id)
                              }
                            >
                              <span>{category.name}</span>
                              {category.badge && (
                                <Badge className="bg-red-500 text-white text-xs">
                                  {category.badge}
                                </Badge>
                              )}
                            </Button>
                          ))}
                        </div>
                      </div>

                      {/* Categories */}
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-3">
                          Shop by Category
                        </h3>
                        <div className="space-y-1">
                          {mobileMenuCategories.map(category => (
                            <Collapsible
                              key={category.id}
                              open={openCategories.includes(category.id)}
                              onOpenChange={() =>
                                toggleMobileCategory(category.id)
                              }
                            >
                              <CollapsibleTrigger asChild>
                                <Button
                                  variant="ghost"
                                  className="w-full justify-between h-12 text-base font-medium rounded-2xl glass-medium border-white/30"
                                >
                                  <div className="flex items-center gap-2">
                                    <span>{category.name}</span>
                                    {category.badge && (
                                      <Badge className="bg-amber-500 text-white text-xs">
                                        {category.badge}
                                      </Badge>
                                    )}
                                  </div>
                                  <ChevronRight
                                    className={`h-4 w-4 transition-transform ${
                                      openCategories.includes(category.id)
                                        ? 'rotate-90'
                                        : ''
                                    }`}
                                  />
                                </Button>
                              </CollapsibleTrigger>
                              <CollapsibleContent className="ml-4 mt-1 space-y-1">
                                {category.items.map((item, itemIndex) => (
                                  <Button
                                    key={item}
                                    variant="ghost"
                                    className="w-full justify-start h-10 text-sm text-gray-600 hover:text-amber-900 rounded-xl transition-all duration-300 glass-light border-amber/20"
                                    onClick={() =>
                                      handleMobileItemClick(category.id, itemIndex)
                                    }
                                  >
                                    {item}
                                  </Button>
                                ))}
                              </CollapsibleContent>
                            </Collapsible>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Mobile CTA */}
                    <div
                      className="p-4 relative"
                      style={{
                        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      <Button
                        className="w-full h-12 rounded-2xl font-semibold text-white transition-all duration-300 hover:scale-105"
                        onClick={handleCatalogClick}
                        style={{
                          background:
                            'linear-gradient(135deg, #d97706 0%, #92400e 100%)',
                          boxShadow: '0 4px 16px rgba(217, 119, 6, 0.3)',
                        }}
                      >
                        {tNav('mobile.viewAllProducts')}
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Desktop Mega Menu */}
            <div
              onMouseEnter={handleMegaMenuEnter}
              onMouseLeave={handleMegaMenuLeave}
              className="hidden lg:block"
            >
              <MegaMenu
                isOpen={isMegaMenuOpen}
                onClose={() => setIsMegaMenuOpen(false)}
              />
            </div>

            {/* Mobile Search Popup */}
            {isMobileSearchPopupOpen && (
              <MobileSearchPopup
                isVisible={isMobileSearchPopupOpen}
                onClose={() => setIsMobileSearchPopupOpen(false)}
                onProductClick={product => {
                  onProductClick?.(product);
                  setIsMobileSearchPopupOpen(false);
                }}
                onViewAllResults={query => {
                  // Сохраняем запрос в Zustand store
                  setSearchStoreQuery(query);
                  // Переходим на страницу поиска
                  navigate(getLocalizedUrl('/search'));
                  setIsMobileSearchPopupOpen(false);
                }}
                onAddToCart={product => {
                  onAddToCart?.(product);
                }}
                initialQuery={mobileSearchQuery}
              />
            )}
          </div>
        </div>
      </header>

      {/* Dark overlay for mobile menu */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          }}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </>
  );
}
